import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { helpers } from '@/client/utils/helpers';
import {
  useMasterDataList,
  useMasterDataDatasets,
  useCreateMasterDataRow,
  useUpdateMasterDataRow,
  useDeleteMasterDataRow,
  useImportMasterData,
} from '@/lib/sdk/master-data/queries';
import { useMasterDataCategories } from '@/lib/sdk/master-data-categories/queries';
import { DEFAULT_COLUMN_CONFIG } from '@/lib/sdk/master-data-categories/types';
import { resolveColumnConfig } from '../components/CategoryManager';
import { useQueryClient } from '@tanstack/react-query';
import { fieldDefinitionsQuery, useFieldDefinitions } from '@/lib/sdk/master-data-field-definitions/queries';
import { MasterDataApi } from '@/lib/sdk/master-data/api';
import type { MasterDataRow } from '@/lib/sdk/master-data/types';
import type { MasterDataFieldDefinition } from '@/lib/sdk/master-data-field-definitions/types';
import type { RowFormData, RowPrefill } from '../components/RowModal';

const PAGE_SIZE = 20;

function kvToFields(kv: { k: string; v: string }[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const { k, v } of kv) {
    if (k.trim()) out[k.trim()] = v;
  }
  return out;
}

// Hidden fields are usually system-set (e.g. writeback targets), so a clone or
// fork must not inherit another row's values for them — same as the Add form.
function copyableFields(row: MasterDataRow, defs: MasterDataFieldDefinition[]): Record<string, unknown> {
  const hidden = new Set(defs.filter((d) => d.hidden).map((d) => d.field_name));
  return Object.fromEntries(Object.entries(row.fields).filter(([k]) => !hidden.has(k)));
}

export function useMasterDataPage() {
  const { currentOrg, user, userPermission, featurePermissions } = useAuth();
  const orgId = currentOrg?.id ?? '';
  const isSuperAdmin = user?.is_super_admin === true;
  const toast = useToast();
  const queryClient = useQueryClient();

  const canAdd              = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'add');
  const canEdit             = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'edit');
  const canDelete           = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'delete');
  const canAddFields        = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'add_fields');
  const canEditFields       = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'edit_fields');
  const canDeleteFields     = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'delete_fields');
  const canAddCategories    = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'add_categories');
  const canEditCategories   = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'edit_categories');
  const canDeleteCategories = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'delete_categories');
  const canManageFields     = canAddFields || canEditFields || canDeleteFields;
  const canManageCategories = canAddCategories || canEditCategories || canDeleteCategories;
  const canImport           = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'import');
  const canExport           = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'master_data', 'export');

  // Pre-selects the dataset from a `?dataset=` query param — used by the old
  // /stores route's redirect (dataset=store) so the link still lands filtered.
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [selectedDataset, setSelectedDataset] = useState<string>(() => searchParams.get('dataset') ?? '');
  const [searchInput, setSearchInput] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [serverSearch, setServerSearch] = useState('');
  const [serverFrom, setServerFrom] = useState('');
  const [serverTo, setServerTo] = useState('');

  const [isRowModalOpen, setIsRowModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<MasterDataRow | null>(null);
  const [forkPrefill, setForkPrefill] = useState<RowPrefill | null>(null);
  const [deletingRow, setDeletingRow] = useState<MasterDataRow | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; updated?: number; errors: string[] } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isFieldSchemaOpen, setIsFieldSchemaOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isBulkEditorOpen, setIsBulkEditorOpen] = useState(false);
  const [bulkEditorMode, setBulkEditorMode] = useState<'create' | 'edit'>('create');
  const [bulkEditorRows, setBulkEditorRows] = useState<MasterDataRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [deletingSelected, setDeletingSelected] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const listParams = useMemo(() => ({
    dataset: selectedDataset || undefined,
    page,
    page_size: PAGE_SIZE,
    search: serverSearch || undefined,
    from: serverFrom ? new Date(serverFrom + 'T00:00:00.000Z').toISOString() : undefined,
    to: serverTo ? new Date(serverTo + 'T23:59:59.999Z').toISOString() : undefined,
  }), [selectedDataset, page, serverSearch, serverFrom, serverTo]);

  const { data, isLoading, refetch } = useMasterDataList(orgId, listParams);
  const { data: datasets } = useMasterDataDatasets(orgId);
  const { data: categories = [] } = useMasterDataCategories(orgId);
  const { data: fieldDefs = [] } = useFieldDefinitions(orgId, selectedDataset);

  const selectedCategory = categories.find((c) => c.name === selectedDataset);
  const labelColumnName = selectedCategory?.label_column_name ?? 'Name';
  // With no category selected the listing spans every dataset, so show everything.
  const columnConfig = selectedDataset ? resolveColumnConfig(selectedCategory) : DEFAULT_COLUMN_CONFIG;

  useEffect(() => {
    if (selectedDataset) return;
    const defaultCat = categories.find((c) => c.is_default);
    if (defaultCat) setSelectedDataset(defaultCat.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const { mutate: createRow, isPending: isCreating } = useCreateMasterDataRow(orgId);
  const { mutate: updateRow, isPending: isUpdating } = useUpdateMasterDataRow(orgId);
  const { mutate: deleteRow, isPending: isDeleting } = useDeleteMasterDataRow(orgId);
  const { mutate: importCsv, isPending: isImporting } = useImportMasterData(orgId);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setServerSearch(value);
      setPage(1);
    }, 300);
  }, []);

  const handleFromChange = useCallback((value: string) => { setFromDate(value); setServerFrom(value); setPage(1); }, []);
  const handleToChange = useCallback((value: string) => { setToDate(value); setServerTo(value); setPage(1); }, []);
  const handleDatasetChange = useCallback((value: string) => { setSelectedDataset(value); setPage(1); setSelectedRowIds([]); }, []);

  const handleOpenAdd = useCallback(() => { setEditingRow(null); setForkPrefill(null); setIsRowModalOpen(true); }, []);
  const handleOpenEdit = useCallback((row: MasterDataRow) => { setEditingRow(row); setForkPrefill(null); setIsRowModalOpen(true); }, []);

  // Fork writes the source's key into parent_key, so it needs a visible Belongs To.
  // A managed_reference dataset's parent_key must point into a different dataset,
  // which the server rejects for a same-dataset parent; that check needs the row's
  // field definitions, known up front only for the selected dataset (handleOpenFork
  // re-checks for rows shown under "All").
  const canForkRow = useCallback((row: MasterDataRow) => {
    if (!canAdd) return false;
    const category = categories.find((c) => c.name === row.dataset);
    if (!resolveColumnConfig(category).show_parent_key) return false;
    return !(row.dataset === selectedDataset && fieldDefs.some((d) => d.data_type === 'managed_reference'));
  }, [canAdd, categories, selectedDataset, fieldDefs]);

  const handleClone = useCallback(async (row: MasterDataRow) => {
    let defs: MasterDataFieldDefinition[];
    try {
      defs = await queryClient.fetchQuery(fieldDefinitionsQuery(orgId, row.dataset));
    } catch (err: any) {
      toast.error(err.message ?? 'Clone failed');
      return;
    }
    createRow(
      {
        dataset: row.dataset,
        label: `${row.label} (copy)`,
        parent_key: row.parent_key ?? null,
        lat: row.lat ?? null,
        lng: row.lng ?? null,
        fields: copyableFields(row, defs),
      },
      {
        onSuccess: (created) => toast.success(`Item cloned as ${created.key}`),
        onError: (err: any) => toast.error(err.message ?? 'Clone failed'),
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleOpenFork = useCallback(async (row: MasterDataRow) => {
    let defs: MasterDataFieldDefinition[];
    try {
      defs = await queryClient.fetchQuery(fieldDefinitionsQuery(orgId, row.dataset));
    } catch (err: any) {
      toast.error(err.message ?? 'Could not load fields for this category');
      return;
    }
    if (defs.some((d) => d.data_type === 'managed_reference')) {
      toast.error('Fork is not available for this category: its Belongs To must point to another category');
      return;
    }
    setEditingRow(null);
    setForkPrefill({
      dataset: row.dataset,
      label: row.label,
      parent_key: row.key,
      parent_label: row.label,
      lat: row.lat ?? undefined,
      lng: row.lng ?? undefined,
      fields: copyableFields(row, defs),
    });
    setIsRowModalOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleRowSubmit = useCallback((formData: RowFormData) => {
    const fields = kvToFields(formData.fields);
    const parent_key = formData.parent_key?.trim() || null;
    const lat = formData.lat ?? null;
    const lng = formData.lng ?? null;
    if (editingRow) {
      updateRow(
        { rowId: editingRow.id, dto: { label: formData.label, fields, parent_key, lat, lng } },
        {
          onSuccess: () => { toast.success('Item updated'); setIsRowModalOpen(false); },
          onError: (err: any) => toast.error(err.message ?? 'Update failed'),
        }
      );
    } else {
      createRow(
        { dataset: formData.dataset, key: formData.key, label: formData.label, fields, parent_key, lat, lng },
        {
          onSuccess: () => { toast.success('Item created'); setIsRowModalOpen(false); },
          onError: (err: any) => toast.error(err.message ?? 'Create failed'),
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRow]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingRow) return;
    deleteRow(deletingRow.id, {
      onSuccess: () => { toast.success('Item deleted'); setDeletingRow(null); },
      onError: (err: any) => toast.error(err.message ?? 'Delete failed'),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletingRow]);

  const handleImport = useCallback((dataset: string, file: File) => {
    importCsv(
      { dataset, file },
      {
        onSuccess: (result) => setImportResult(result),
        onError: (err: any) => toast.error(err.message ?? 'Import failed'),
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = useCallback(async () => {
    if (!orgId) return;
    setIsExporting(true);
    try {
      await MasterDataApi.exportExcel(orgId, {
        dataset: selectedDataset || undefined,
        search:  serverSearch   || undefined,
        from:    serverFrom ? new Date(serverFrom + 'T00:00:00.000Z').toISOString() : undefined,
        to:      serverTo   ? new Date(serverTo   + 'T23:59:59.999Z').toISOString() : undefined,
      });
    } catch (err: any) {
      toast.error(err.message ?? 'Export failed');
    } finally {
      setIsExporting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, selectedDataset, serverSearch, serverFrom, serverTo]);

  const handleAddMultiple = useCallback(() => {
    setBulkEditorMode('create');
    setBulkEditorRows([]);
    setIsBulkEditorOpen(true);
  }, []);

  const handleEditSelected = useCallback(() => {
    const selected = rows.filter((r) => selectedRowIds.includes(String(r.id)));
    setBulkEditorMode('edit');
    setBulkEditorRows(selected);
    setIsBulkEditorOpen(true);
  }, [rows, selectedRowIds]);

  const openDeleteSelected = useCallback(() => setDeletingSelected(true), []);

  const handleDeleteSelected = useCallback(async () => {
    const ids = selectedRowIds.map(Number);
    const CONCURRENCY = 5;
    let failed = 0;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const batch = ids.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map((id) => MasterDataApi.remove(orgId, id))
      );
      failed += results.filter((r) => r.status === 'rejected').length;
    }
    const deleted = ids.length - failed;
    if (deleted > 0) toast.success(`${deleted} item${deleted !== 1 ? 's' : ''} deleted`);
    if (failed > 0) toast.error(`${failed} item${failed !== 1 ? 's' : ''} could not be deleted`);
    setDeletingSelected(false);
    setSelectedRowIds([]);
    refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, selectedRowIds]);

  return {
    orgId,
    canAdd, canEdit, canDelete,
    canManageFields, canManageCategories,
    canImport, canExport,

    selectedDataset, handleDatasetChange,
    searchInput, handleSearchChange,
    fromDate, handleFromChange,
    toDate, handleToChange,

    page, setPage, pageCount, total,

    rows, isLoading, refetch,
    datasets,
    fieldDefs,
    labelColumnName,
    columnConfig,

    isRowModalOpen, setIsRowModalOpen,
    editingRow,
    forkPrefill,
    handleOpenAdd, handleOpenEdit,
    handleClone, handleOpenFork, canForkRow,
    handleRowSubmit,
    isCreating, isUpdating,

    deletingRow, setDeletingRow,
    handleDeleteConfirm,
    isDeleting,

    isImportOpen, setIsImportOpen,
    importResult, setImportResult,
    handleImport,
    isImporting,

    isExporting, handleExport,

    isFieldSchemaOpen, setIsFieldSchemaOpen,
    isCategoryManagerOpen, setIsCategoryManagerOpen,

    isBulkEditorOpen, setIsBulkEditorOpen,
    bulkEditorMode, bulkEditorRows,
    handleAddMultiple, handleEditSelected, openDeleteSelected,

    selectedRowIds, setSelectedRowIds,
    deletingSelected, setDeletingSelected,
    handleDeleteSelected,
  };
}
